import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Table } from 'antd'
import type { TablePaginationConfig } from 'antd/es/table'
import { FilterOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons'
import Button from 'components/Button'
import FilterDrawer from 'components/FilterDrawer/FilterDrawer'
import { Filter } from 'components/FilterDrawer/FilterDrawer.config'
import Icon from 'components/Icon'
import Tooltip from 'components/Tooltip'
import Search from 'UI/Search/Search'
import { useDebounce } from 'hooks/useDebounce'
import { useFilterOptionsQuery } from 'hooks/useFilterOptionsQuery'
import useVerticalSearchAndSort from 'hooks/useVerticalSearchAndSort'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { employeesExportCount, EMPLOYEES_EXPORT_TYPES, EmployeesExportType } from 'services/employeesApi'
import { onEmployeesCSVExport } from 'utils/employees'
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, filtersInitialState } from './EmployeesTab.config'
import styles from './EmployeesTab.module.scss'
import ExportPopover from './ExportPopover'
import useEmployeesColumns, { EmployeeRow } from './useEmployeesColumns'
import { useEmployeesQuery } from './useEmployeesQuery'

interface EmployeesTabProps {
  caseId: string
  verticalEntity?: any
  activityIndices: string
}

const SEARCH_PLACEHOLDER = 'Search by name, url, job role, location or description'

const hasSubRows = (record: EmployeeRow): boolean =>
  Array.isArray(record.subRows) && record.subRows.length > 0

const getRowKey = (record: EmployeeRow, index?: number): string =>
  String(record.indexId ?? record.employeeId ?? index ?? '')

const HORIZONTAL_SCROLLBAR_BUFFER = 12
const MIN_TABLE_BODY_HEIGHT = 200
const MIN_WRAPPER_HEIGHT = 320
const FALLBACK_HEADER_HEIGHT = 46
const FALLBACK_PAGINATION_HEIGHT = 64
const VIEWPORT_BOTTOM_RESERVE = 32

interface MeasuredHeights {
  wrapperHeight: number
  bodyHeight: number
}

const measureHeights = (wrapper: HTMLElement): MeasuredHeights => {
  const rect = wrapper.getBoundingClientRect()
  const header = wrapper.querySelector('.ant-table-thead') as HTMLElement | null
  const pagination = wrapper.querySelector('.ant-table-pagination') as HTMLElement | null

  const headerHeight = header?.offsetHeight ?? FALLBACK_HEADER_HEIGHT
  let paginationHeight = FALLBACK_PAGINATION_HEIGHT
  if (pagination) {
    const computed = window.getComputedStyle(pagination)
    paginationHeight =
      pagination.offsetHeight + parseFloat(computed.marginTop || '0') + parseFloat(computed.marginBottom || '0')
  }

  const wrapperHeight = Math.max(window.innerHeight - rect.top - VIEWPORT_BOTTOM_RESERVE, MIN_WRAPPER_HEIGHT)
  const bodyHeight = Math.max(
    wrapperHeight - headerHeight - paginationHeight - HORIZONTAL_SCROLLBAR_BUFFER,
    MIN_TABLE_BODY_HEIGHT,
  )

  return { wrapperHeight, bodyHeight }
}

const EmployeesTab = ({ caseId, verticalEntity, activityIndices }: EmployeesTabProps) => {
  const match = useRouteMatch<{ vertical: string; caseId: string }>('/vertical/:vertical/:caseId')
  const vertical = match?.params.vertical || ''

  const tableWrapperRef = useRef<HTMLDivElement | null>(null)
  const [{ wrapperHeight: tableWrapperHeight, bodyHeight: tableBodyHeight }, setMeasuredHeights] =
    useState<MeasuredHeights>({ wrapperHeight: MIN_WRAPPER_HEIGHT, bodyHeight: MIN_TABLE_BODY_HEIGHT })

  const { searchValue, searchValueDebounced, setSearchValue } = useVerticalSearchAndSort()

  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const [activeFilters, setActiveFilters] = useState<Filter[]>([])
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])

  const activeFiltersDebounced = useDebounce(activeFilters, 500)

  const [isExportPopoverOpen, setIsExportPopoverOpen] = useState(false)
  const [exportCount, setExportCount] = useState<number | null>(null)
  const [isCountLoading, setIsCountLoading] = useState(false)
  const [exportType, setExportType] = useState<EmployeesExportType>(EMPLOYEES_EXPORT_TYPES.SUMMARY)

  useEffect(() => {
    setPageIndex(0)
  }, [searchValueDebounced, activeFiltersDebounced])

  const { verticalDocumentActivity, isFetching, total } = useEmployeesQuery({
    caseId,
    zoneActivityIndices: activityIndices,
    pageSize,
    searchValue: searchValueDebounced,
    filters: activeFiltersDebounced,
    sort: [],
    page: pageIndex,
  })

  const { filterOptions } = useFilterOptionsQuery({
    verticalIndices: activityIndices,
    currentQueryKey: 'employees-filter-options-key',
    filtersInitialState,
    entityId: caseId,
  })

  const fetchExportCount = useCallback(async () => {
    setIsCountLoading(true)
    setExportCount(null)

    try {
      const count = await employeesExportCount({
        employeesIndices: activityIndices,
        entityId: caseId,
        filters: activeFiltersDebounced,
        searchValue: searchValueDebounced,
      })
      setExportCount(count)
    } finally {
      setIsCountLoading(false)
    }
  }, [activityIndices, caseId, activeFiltersDebounced, searchValueDebounced])

  const handleExportClick = useCallback(() => {
    setExportType(EMPLOYEES_EXPORT_TYPES.SUMMARY)
    setIsExportPopoverOpen(true)
    fetchExportCount()
  }, [fetchExportCount])

  const handleExportConfirm = useCallback(
    (selectedExportType: EmployeesExportType) => {
      onEmployeesCSVExport({
        employeesIndices: activityIndices,
        entityId: caseId,
        vertical,
        tin: verticalEntity?.entityDetails?.tin,
        onlineName: verticalEntity?.entityDetails?.name,
        filters: activeFiltersDebounced,
        searchValue: searchValueDebounced,
        exportType: selectedExportType,
      })
    },
    [activityIndices, caseId, vertical, verticalEntity, activeFiltersDebounced, searchValueDebounced],
  )

  const columns = useEmployeesColumns()
  const dataSource = useMemo(
    () => (verticalDocumentActivity as EmployeeRow[] | null) ?? [],
    [verticalDocumentActivity],
  )

  const expandableRowKeys = useMemo(
    () => dataSource.filter(hasSubRows).map((record, index) => getRowKey(record, index)),
    [dataSource],
  )

  useEffect(() => {
    setExpandedRowKeys([])
  }, [pageIndex, pageSize, searchValueDebounced, activeFiltersDebounced])

  const isAllExpanded = expandableRowKeys.length > 0 && expandedRowKeys.length === expandableRowKeys.length
  const toggleExpandAll = useCallback(
    () => setExpandedRowKeys(isAllExpanded ? [] : expandableRowKeys),
    [isAllExpanded, expandableRowKeys],
  )

  const recomputeHeights = useCallback(() => {
    const node = tableWrapperRef.current
    if (!node) return
    setMeasuredHeights(prev => {
      const next = measureHeights(node)
      if (next.wrapperHeight === prev.wrapperHeight && next.bodyHeight === prev.bodyHeight) return prev
      return next
    })
  }, [])

  useLayoutEffect(() => {
    const node = tableWrapperRef.current
    if (!node) return undefined

    recomputeHeights()
    const observer = new ResizeObserver(recomputeHeights)
    observer.observe(node)
    window.addEventListener('resize', recomputeHeights)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', recomputeHeights)
    }
  }, [recomputeHeights])

  useEffect(() => {
    recomputeHeights()
  }, [recomputeHeights, isFetching, total, pageSize])

  const pagination = useMemo<TablePaginationConfig>(
    () => ({
      current: pageIndex + 1,
      pageSize,
      total,
      showSizeChanger: true,
      showQuickJumper: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
      onChange: (nextPage, nextPageSize) => {
        const sizeChanged = nextPageSize !== pageSize
        setPageSize(nextPageSize ?? DEFAULT_PAGE_SIZE)
        setPageIndex(sizeChanged ? 0 : nextPage - 1)
      },
    }),
    [pageIndex, pageSize, total],
  )

  const rangeStart = total > 0 ? pageIndex * pageSize + 1 : 0
  const rangeEnd = Math.min((pageIndex + 1) * pageSize, total)

  const renderExpandedRow = useCallback(
    (record: EmployeeRow) => {
      const parentKey = getRowKey(record)
      return (
        <Table<EmployeeRow>
          className={styles.subTable}
          columns={columns}
          dataSource={(record.subRows as EmployeeRow[] | undefined) ?? []}
          pagination={false}
          showHeader={false}
          size='middle'
          rowKey={(_sub, subIdx) => `${parentKey}-sub-${subIdx ?? 0}`}
        />
      )
    },
    [columns],
  )

  const expandable = useMemo(
    () => ({
      columnWidth: 48,
      rowExpandable: hasSubRows,
      expandedRowRender: renderExpandedRow,
      expandedRowKeys,
      onExpandedRowsChange: (keys: readonly React.Key[]) => setExpandedRowKeys([...keys]),
      columnTitle:
        expandableRowKeys.length > 0 ? (
          <Tooltip title={isAllExpanded ? 'Collapse all' : 'Expand all'}>
            <button
              type='button'
              className={styles.expandToggle}
              onClick={toggleExpandAll}
              aria-label={isAllExpanded ? 'Collapse all experiences' : 'Expand all experiences'}
            >
              {isAllExpanded ? <MinusOutlined style={{ fontSize: 12 }} /> : <PlusOutlined style={{ fontSize: 12 }} />}
            </button>
          </Tooltip>
        ) : null,
      expandIcon: ({
        expanded,
        onExpand,
        record,
        expandable: isExpandable,
      }: {
        expanded: boolean
        onExpand: (record: EmployeeRow, e: React.MouseEvent<HTMLElement>) => void
        record: EmployeeRow
        expandable: boolean
      }) => {
        if (!isExpandable) return null
        const Glyph = expanded ? MinusOutlined : PlusOutlined
        return (
          <button
            type='button'
            className={styles.expandToggle}
            onClick={e => {
              e.stopPropagation()
              onExpand(record, e)
            }}
            aria-label={expanded ? 'Collapse experiences' : 'Expand experiences'}
          >
            <Glyph style={{ fontSize: 12 }} />
          </button>
        )
      },
    }),
    [renderExpandedRow, expandedRowKeys, expandableRowKeys, isAllExpanded, toggleExpandAll],
  )

  return (
    <Stack direction='column' className={styles.mainStack}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <h2>Employees List</h2>
          {total > 0 && (
            <Typography variant='body2' sx={{ color: '#7E8BA6', fontFamily: 'Source Sans Pro', fontSize: '14px' }}>
              Showing {rangeStart}&ndash;{rangeEnd} of {total} employees
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ExportPopover
            open={isExportPopoverOpen}
            onClose={() => setIsExportPopoverOpen(false)}
            onExport={handleExportConfirm}
            activeFilters={activeFiltersDebounced}
            searchValue={searchValueDebounced}
            exportCount={exportCount}
            isCountLoading={isCountLoading}
            exportType={exportType}
            onExportTypeChange={setExportType}
          >
            <Tooltip title='Export Entity Employees CSV'>
              <Button buttonType='icon' className={styles.actionButton} onClick={handleExportClick}>
                <Icon name='share' size='regular' />
              </Button>
            </Tooltip>
          </ExportPopover>
          <Search
            allowClear
            placeholder={SEARCH_PLACEHOLDER}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            className={styles.search}
          />
          <Tooltip title='Filter employees'>
            <Button
              buttonType='secondary-icon'
              className={styles.actionButton}
              onClick={() => setIsFilterDrawerOpen(prev => !prev)}
              icon={<FilterOutlined />}
            />
          </Tooltip>
        </Box>
      </Box>
      {filterOptions && (
        <FilterDrawer
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          filtersInitialState={filtersInitialState}
          filterOptions={filterOptions}
        />
      )}
      <Box ref={tableWrapperRef} className={styles.tableWrapper} sx={{ height: tableWrapperHeight }}>
        <Table<EmployeeRow>
          rowKey={getRowKey}
          columns={columns}
          dataSource={dataSource}
          loading={isFetching}
          pagination={pagination}
          scroll={{ x: 'max-content', y: tableBodyHeight }}
          size='middle'
          expandable={expandable}
        />
      </Box>
    </Stack>
  )
}

export default EmployeesTab
